import { defineComponent as _defineComponent } from 'vue'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import BKImage from '@/components/AppShared/BKImage.vue'
import { useImage } from '@/shared/composables/image'
import { ref } from 'vue'
import interactionPlugin from '@fullcalendar/interaction'
import { CalendarOptions, EventSegment, EventSourceFuncArg, MoreLinkArg, MoreLinkContentArg } from '@fullcalendar/core'
import { eventApi, EventDB } from '@/enitites/event'


export default /*#__PURE__*/_defineComponent({
  __name: 'CalendarDesktop',
  setup(__props) {

function getOffsetDate(date: string) {
  const newDate = new Date(date)
  const offsetDate = newDate.getTimezoneOffset()
  return new Date(newDate.getTime() - offsetDate * 60000).toISOString().split('T')[0]
}

const activeMobileList = ref(false)
const modalEventsData = ref<{ id: string; title: string; start: Date | null; img: string }[]>([])
const clickedDateEl = ref<HTMLElement | null>(null)
const calendar = ref<InstanceType<typeof FullCalendar> | null>(null)

function changePopoverPosition(cell: HTMLElement | null) {
  if (!cell) return
  setTimeout(() => {
    const popover: HTMLElement | null = document.querySelector('.fc-popover')
    const popoverContainerRect: DOMRect | undefined = document
      .querySelector('.fc-view-harness')
      ?.getBoundingClientRect()
    if (!popover || !popoverContainerRect) return
    const { width: calendarCellWidth } = cell.getBoundingClientRect()
    const { bottom: popoverBottomPosition, right: popoverRightPosition } = popover.getBoundingClientRect()
    const { bottom: popoverContainerBottomPosition, right: popoverContainerRightPosition } = popoverContainerRect
    const padding = 20
    const currentPopoverLeftPosition = parseFloat(popover.style.left)

    const isPopoverOverflowBottom = popoverBottomPosition > popoverContainerBottomPosition
    if (isPopoverOverflowBottom) {
      const differenceBottomPositions = popoverBottomPosition - popoverContainerBottomPosition
      const currentTop = parseFloat(popover.style.top)
      popover.style.top = `${currentTop - differenceBottomPositions - padding}px`
    }

    if (Math.abs(popoverRightPosition - popoverContainerRightPosition) < calendarCellWidth) {
      popover.style.left = `${currentPopoverLeftPosition - calendarCellWidth}px`
      return
    }

    const isPopoverOverflowRight = popoverRightPosition > popoverContainerRightPosition

    if (isPopoverOverflowRight) {
      const differenceRightPositions = popoverRightPosition - popoverContainerRightPosition
      popover.style.left = `${currentPopoverLeftPosition - differenceRightPositions - calendarCellWidth}px`
    } else {
      popover.style.left = `${currentPopoverLeftPosition + calendarCellWidth}px`
    }
  }, 100)
}

function updateCellClassName(cell: HTMLElement | null) {
  if (clickedDateEl.value) clickedDateEl.value.classList.remove('day-clicked')
  clickedDateEl.value = cell
  clickedDateEl.value?.classList.add('day-clicked')
}

const calendarOptions: CalendarOptions = {
  initialView: 'dayGridMonth',
  plugins: [dayGridPlugin, interactionPlugin],
  headerToolbar: {
    left: '',
    center: 'prev title next',
    end: '',
  },
  events: async (fetchInfo: EventSourceFuncArg) => {
    const start = getOffsetDate(fetchInfo.startStr)
    const end = getOffsetDate(fetchInfo.endStr)

    const data = await eventApi.getEvents({
      pageNumber: 0,
      pageSize: 999,
      start,
      end,
    })

    if (!data || typeof data === 'string') return []
    const publishedEvents = data.filter((event: EventDB) => event.status === 'published')

    return publishedEvents.map((event: EventDB) => ({
      id: event.id,
      title: event.title,
      start: event.dateStart,
      img: event.image,
      optionalEnd: event.dateEnd,
    }))
  },
  eventColor: 'transparent',
  eventBackgroundColor: 'transparent',
  eventDisplay: 'block',
  dayMaxEvents: true,
  dayHeaderFormat: {
    weekday: 'long',
  },
  eventTimeFormat: {
    hour: 'numeric',
    minute: '2-digit',
    meridiem: 'short',
  },
  views: {
    dayGridMonth: {
      dayMaxEvents: 4,
    },
  },
  fixedWeekCount: false,
  dayPopoverFormat: {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  },
  moreLinkContent: (args: MoreLinkContentArg) => `+${args.num}`,
  moreLinkClick: (info: MoreLinkArg) => {
    const dayEl = (info.jsEvent.target as HTMLElement)?.offsetParent?.offsetParent.parentElement
    updateCellClassName(dayEl)
    // wait for render of popover to calculate popover position
    changePopoverPosition(dayEl)

    modalEventsData.value = info.allSegs.map((event: EventSegment) => {
      return {
        id: event.event.id,
        title: event.event.title,
        start: event.event.start,
        img: event.event.extendedProps.img,
        optionalEnd: event.event.extendedProps.optionalEnd,
      }
    })
    return 'popover'
  },
  eventWillUnmount: () => {
    clickedDateEl.value?.classList.remove('day-clicked')
  },
  expandRows: true,
}

const { getImageById } = useImage()

return { __sfc: true,getOffsetDate, activeMobileList, modalEventsData, clickedDateEl, calendar, changePopoverPosition, updateCellClassName, calendarOptions, getImageById, FullCalendar, BKImage }
}

})